import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import type { ComposerSeed } from "./AssistantComposer";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantExamples } from "./AssistantExamples";
import { AssistantConversationHeader } from "./AssistantConversationHeader";
import { AssistantWelcome } from "./AssistantWelcome";
import { AssistantLayout } from "./AssistantLayout";
import { CHAT_CONTENT_WIDTH, CHAT_TEXT } from "./assistantChatLayout";
import type { AssistantStartState } from "../../shared/assistantAccess";
import { useAssistantAccess } from "../../shared/assistantAccess";
import type { AssistantConversationState } from "./navigationState";

export const AssistantStartPage = () => {
  usePrivacyDocumentTitle("Assistant.Title");
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const hasAccess = useAssistantAccess();
  const startsRegistration = (useLocation().state as AssistantStartState)?.startsRegistration === true;
  const startedRef = useRef(false);
  const [seed, setSeed] = useState<ComposerSeed | null>(null);

  const start = useCallback(
    (text: string) => {
      const state: AssistantConversationState = { firstTurn: { text, idempotencyKey: crypto.randomUUID() } };
      navigate(paths.assistantConversation(crypto.randomUUID()), { replace: true, state });
    },
    [navigate, paths]
  );

  useEffect(() => {
    if (!hasAccess || !startsRegistration || startedRef.current) return;
    startedRef.current = true;
    start(t("Assistant.Start.FirstMessage"));
  }, [hasAccess, startsRegistration, start, t]);

  return (
    <AssistantLayout>
      <AssistantConversationHeader />
      <Stack sx={{ flex: 1, minHeight: 0, ...CHAT_TEXT }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Box sx={{ p: 4, width: "100%", maxWidth: CHAT_CONTENT_WIDTH, mx: "auto" }}>
            <AssistantWelcome isLarge />
          </Box>
        </Box>
        <Stack sx={{ gap: 1, px: 4, pt: 1, pb: 3, width: "100%", maxWidth: CHAT_CONTENT_WIDTH, mx: "auto" }}>
          {hasAccess && !startsRegistration && (
            <AssistantExamples onPick={text => setSeed(current => ({ key: (current?.key ?? 0) + 1, text }))} />
          )}
          <AssistantComposer disabled={!hasAccess || startsRegistration} onSend={start} seed={seed} />
        </Stack>
      </Stack>
    </AssistantLayout>
  );
};
